import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Users, MapPin, Mail, MessageCircle, StickyNote, Sparkles } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { useVoz, MicButton } from "../ai/Voz";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

export const TIPOS = [["llamada", "Llamada", Phone], ["reunion", "Reunión", Users], ["visita", "Visita", MapPin], ["correo", "Correo", Mail], ["whatsapp", "WhatsApp", MessageCircle], ["nota", "Nota", StickyNote]];
export const TL = Object.fromEntries(TIPOS.map(([k, l]) => [k, l]));
const inp = "w-full rounded-xl border border-[#E2DDD5] bg-white px-3 py-2 text-sm focus:border-[#0B2545] focus:outline-none";
const VACIO = { tipo: "llamada", resultado: "", siguiente_paso: "", siguiente_fecha: "", duracion_min: 0 };

export const QuickLog = ({ entidad, refId, onSaved, prefix = "ql" }) => {
  const [f, setF] = useState(VACIO);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const voz = useVoz(async (texto) => {
    try { const r = await http.post("/actividad/resumir", { texto }); setAi(r.data); } catch (e) { toast.error(errMsg(e)); }
  }, (m) => toast.error(m));
  const aceptar = () => { setF({ ...f, tipo: ai.tipo, resultado: [ai.resumen, ai.resultado].filter(Boolean).join("\n"), siguiente_paso: ai.siguiente_paso }); setAi(null); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await http.post("/actividad", { ...f, entidad, ref: refId, siguiente_fecha: f.siguiente_fecha || null, duracion_min: Number(f.duracion_min) || 0 });
      toast.success(r.data.tarea_id ? "Actividad registrada · tarea creada" : "Actividad registrada");
      setF(VACIO); onSaved?.(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid={`${prefix}-form`}>
      <div className="flex flex-wrap gap-1.5">{TIPOS.map(([k, l, I]) => (
        <button key={k} type="button" onClick={() => setF({ ...f, tipo: k })} data-testid={`${prefix}-tipo-${k}`}
          className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${f.tipo === k ? "bg-[#0B2545] text-white" : "bg-[#F2F0EC] text-[#5C5850] hover:bg-[#E2DDD5]"}`}><I size={13} />{l}</button>))}</div>
      <div className="flex items-start gap-2">
        <textarea rows={3} value={f.resultado} onChange={(e) => setF({ ...f, resultado: e.target.value })} placeholder="¿Qué pasó? Resultado de la actividad" className={inp} data-testid={`${prefix}-resultado`} />
        <div className="flex flex-col items-center gap-1"><MicButton voz={voz} /><span className="text-[10px] text-[#8C867A]">Nota de voz</span></div>
      </div>
      {ai && <div className="rounded-xl border border-[#13315C]/20 bg-[#13315C]/5 p-3 text-xs" data-testid={`${prefix}-ai-propuesta`}>
        <p className="mb-1 flex items-center gap-1 font-semibold text-[#13315C]"><Sparkles size={13} />Propuesta de la IA · revisa antes de guardar</p>
        <p><b>{TL[ai.tipo]}</b> · {ai.resumen}</p>{ai.resultado && <p>Resultado: {ai.resultado}</p>}{ai.siguiente_paso && <p>Siguiente paso: {ai.siguiente_paso}</p>}
        <div className="mt-2 flex gap-2"><button onClick={aceptar} className="rounded-full bg-[#0B2545] px-3 py-1 font-semibold text-white" data-testid={`${prefix}-ai-aceptar`}>Usar propuesta</button>
          <button onClick={() => setAi(null)} className="rounded-full px-3 py-1 text-[#5C5850] hover:bg-white" data-testid={`${prefix}-ai-descartar`}>Descartar</button></div>
        <p className="mt-1 text-[10px] text-[#8C867A]">El audio no se guarda; solo el texto que confirmes.</p></div>}
      <div className="grid gap-2 sm:grid-cols-[1fr_150px_100px]">
        <input value={f.siguiente_paso} onChange={(e) => setF({ ...f, siguiente_paso: e.target.value })} placeholder="Siguiente paso (crea una tarea)" className={inp} data-testid={`${prefix}-siguiente`} />
        <input type="date" value={f.siguiente_fecha} onChange={(e) => setF({ ...f, siguiente_fecha: e.target.value })} className={inp} data-testid={`${prefix}-siguiente-fecha`} />
        <input type="number" min={0} value={f.duracion_min} onChange={(e) => setF({ ...f, duracion_min: e.target.value })} title="Duración (min)" className={inp} data-testid={`${prefix}-duracion`} />
      </div>
      <button onClick={save} disabled={busy || f.resultado.trim().length < 2} data-testid={`${prefix}-guardar`}
        className="w-full rounded-full bg-[#E8643C] px-5 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-40 sm:w-auto">Registrar actividad</button>
    </div>
  );
};

export const abrirQuickLog = (d) => window.dispatchEvent(new CustomEvent("quicklog", { detail: d }));

export const QuickLogHost = () => {
  const [d, setD] = useState(null);
  useEffect(() => { const h = (e) => setD(e.detail); window.addEventListener("quicklog", h); return () => window.removeEventListener("quicklog", h); }, []);
  return (
    <Dialog open={!!d} onOpenChange={(o) => !o && setD(null)}>
      <DialogContent className="max-w-lg" data-testid="quicklog-dialog">
        <DialogHeader><DialogTitle>Registrar actividad · {d?.nombre}</DialogTitle><DialogDescription className="sr-only">Registro rápido de actividad comercial</DialogDescription></DialogHeader>
        {d && <QuickLog entidad={d.entidad} refId={d.ref} prefix="qlk" onSaved={() => setD(null)} />}
      </DialogContent>
    </Dialog>
  );
};
